import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchPagesList } from '@/store/pages/thunks';
import { selectPagesList, selectIsPagesLoading } from '@/store/pages/selectors';
import type { InteractionEvent } from '@builder4/shared/types/craft';

export type LinkOptionsType = {
  newTab: boolean;
  useNofollow: boolean;
};

export interface UseInteractionEventOptions {
  event: InteractionEvent | null | undefined;
  defaultEventType: string;
}

export const useInteractionEvent = ({ event, defaultEventType }: UseInteractionEventOptions) => {
  const dispatch = useAppDispatch();
  const pages = useAppSelector(selectPagesList);
  const isPagesLoading = useAppSelector(selectIsPagesLoading);

  useEffect(() => {
    dispatch(fetchPagesList());
  }, [dispatch]);

  const eventType = event?.eventType || defaultEventType;
  const optionId = event?.eventData?.optionId || '';
  const externalUrl = event?.eventData?.url || '';
  const linkOptions: LinkOptionsType = {
    newTab: !!event?.eventData?.newTab,
    useNofollow: !!event?.eventData?.useNofollow,
  };

  const constructLinkedPageUrl = (pageId: string) => {
    const page = pages.find((page) => page.id === pageId);
    if (!page || !page.domain || !page.path) return '';
    return `https://${page.domain}/${page.path}`;
  };

  const filteredPages = useMemo(() => pages.filter((page) => page.domain && page.path), [pages]);

  return {
    eventType,
    optionId,
    externalUrl,
    linkOptions,
    pages: filteredPages,
    isPagesLoading,
    constructLinkedPageUrl,
  };
};
